import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, Info } from 'lucide-react'
import * as Switch from '@radix-ui/react-switch'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Skeleton'
import { cn } from '@/lib/cn'
import {
  useClearDnd,
  usePreferences,
  useSetDnd,
  useUpdatePreferences,
} from '@/hooks/useNotifications'
import type {
  NotificationChannel,
  NotificationKind,
  NotificationPreferences,
} from './types'

const KIND_LABEL: Record<NotificationKind, string> = {
  alert_assigned: 'Alert assigned to me',
  alert_status_changed: 'Alert status changed',
  sla_warning: 'SLA warning',
  block_request_pending: 'Block awaiting my approval',
  block_approved: 'My block approved',
  block_rejected: 'My block rejected',
  case_assigned: 'Case assigned to me',
  case_unassigned: 'Case unassigned from me',
  sar_filed: 'SAR filed',
  mention: 'I was mentioned',
  system: 'System notices',
}

const CHANNELS: { value: NotificationChannel; label: string }[] = [
  { value: 'in_app', label: 'In-app' },
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
]

const DND_PRESETS = [
  { label: '15 min', minutes: 15 },
  { label: '30 min', minutes: 30 },
  { label: '1 h', minutes: 60 },
  { label: '2 h', minutes: 120 },
] as const

/**
 * AF-134 — per-user notification preferences.
 *
 *   - Matrix of kind × channel toggles. Local edits buffer until the
 *     user clicks Save (so we don't fire 33 separate PUTs).
 *   - SMS channel column shows a "critical only" hint and starts off
 *     by default per AC; flipping it on surfaces an explanatory note.
 *   - DND presets (15m/30m/1h/2h). Active until the timestamp clears
 *     server-side.
 */
export function NotificationPreferencesPage() {
  const { data, isLoading, isError } = usePreferences()
  const setDnd = useSetDnd()
  const clearDnd = useClearDnd()

  if (isLoading) return <Skeleton className="h-96 w-full" />
  if (isError || !data) {
    return (
      <Card>
        <CardContent>
          <p className="text-sm text-muted-fg">Couldn't load preferences.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <Header />
      <DndCard
        enabled={data.dnd.enabled}
        until={data.dnd.until}
        onSet={(minutes) => setDnd.mutate({ duration_minutes: minutes })}
        onClear={() => clearDnd.mutate()}
        busy={setDnd.isPending || clearDnd.isPending}
      />
      {/*
       * Editor is keyed on the server-side version of preferences so a
       * fresh server payload remounts it with the new initial — this
       * keeps the in-flight edit logic out of useEffect (per
       * react-hooks/set-state-in-effect lint rule).
       */}
      <Editor key={JSON.stringify(data.preferences)} initial={data.preferences} />
    </div>
  )
}

function Header() {
  return (
    <>
      <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm text-muted-fg">
        <Link to="/notifications" className="rounded px-1 py-0.5 hover:bg-muted hover:text-fg">
          <span className="inline-flex items-center gap-1">
            <ChevronLeft size={12} aria-hidden /> Notifications
          </span>
        </Link>
      </nav>
      <header>
        <h1 className="text-xl font-semibold">Notification preferences</h1>
        <p className="text-sm text-muted-fg">
          Choose what you want to hear about and where. SMS is reserved for
          critical alerts only.
        </p>
      </header>
    </>
  )
}

function Editor({ initial }: { initial: NotificationPreferences }) {
  const update = useUpdatePreferences()
  const [draft, setDraft] = useState<NotificationPreferences>(initial)
  const [smsEverOn, setSmsEverOn] = useState(false)

  const dirty = JSON.stringify(draft) !== JSON.stringify(initial)

  const setEntry = (
    kind: NotificationKind,
    channel: NotificationChannel,
    next: boolean,
  ) => {
    setDraft((cur) => ({
      ...cur,
      [kind]: { ...cur[kind], [channel]: next },
    }))
    if (channel === 'sms' && next) setSmsEverOn(true)
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Channels by notification type</CardTitle>
        </CardHeader>
        <CardContent>
          {smsEverOn && (
            <p className="mb-3 inline-flex items-center gap-1 rounded border border-status-pending/30 bg-status-pending/5 px-2 py-1 text-xs text-status-pending">
              <Info size={12} aria-hidden /> Note: SMS is for critical
              notifications only — non-critical SMS toggles will be ignored
              by the carrier gateway.
            </p>
          )}
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-muted-fg">
              <tr>
                <th scope="col" className="px-3 py-2 text-left font-medium">Type</th>
                {CHANNELS.map((c) => (
                  <th scope="col" key={c.value} className="px-3 py-2 text-center font-medium">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(Object.keys(KIND_LABEL) as NotificationKind[]).map((k) => (
                <tr key={k} className="border-t border-border">
                  <th scope="col" className="px-3 py-2 text-left text-sm font-medium">
                    {KIND_LABEL[k]}
                  </th>
                  {CHANNELS.map((c) => (
                    <td key={c.value} className="px-3 py-2 text-center">
                      <ChannelSwitch
                        kindLabel={KIND_LABEL[k]}
                        channelLabel={c.label}
                        checked={draft[k][c.value]}
                        onCheckedChange={(v) => setEntry(k, c.value, v)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button
          variant="ghost"
          onClick={() => setDraft(initial)}
          disabled={!dirty || update.isPending}
        >
          Reset
        </Button>
        <Button
          onClick={() => update.mutate(draft)}
          disabled={!dirty || update.isPending}
        >
          {update.isPending ? 'Saving…' : 'Save preferences'}
        </Button>
      </div>
    </>
  )
}

function ChannelSwitch({
  kindLabel,
  channelLabel,
  checked,
  onCheckedChange,
}: {
  kindLabel: string
  channelLabel: string
  checked: boolean
  onCheckedChange: (v: boolean) => void
}) {
  return (
    <Switch.Root
      checked={checked}
      onCheckedChange={onCheckedChange}
      aria-label={`${channelLabel} notifications for ${kindLabel}`}
      className={cn(
        'inline-flex h-5 w-9 items-center rounded-full transition-colors data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted',
      )}
    >
      <Switch.Thumb className="block h-4 w-4 translate-x-0.5 rounded-full bg-card shadow transition-transform data-[state=checked]:translate-x-[1.125rem]" />
    </Switch.Root>
  )
}

function DndCard({
  enabled,
  until,
  onSet,
  onClear,
  busy,
}: {
  enabled: boolean
  until: string | null
  onSet: (minutes: number) => void
  onClear: () => void
  busy: boolean
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Do not disturb</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <p className="text-muted-fg">
          Suppress non-critical notifications and toasts. Critical alerts
          (assignments + SLA breaches) still come through so you don't miss
          something urgent.
        </p>
        {enabled && until ? (
          <div className="flex items-center justify-between rounded border border-status-pending/30 bg-status-pending/5 px-3 py-2 text-sm">
            <span className="text-status-pending">
              On until <strong>{new Date(until).toLocaleTimeString()}</strong>
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={onClear}
              disabled={busy}
            >
              Turn off
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-muted-fg">
              Snooze
            </span>
            {DND_PRESETS.map((p) => (
              <Button
                key={p.minutes}
                size="sm"
                variant="outline"
                onClick={() => onSet(p.minutes)}
                disabled={busy}
              >
                {p.label}
              </Button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
